import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('electricProvider');
export interface ElectricProviderInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  accountNumber?: string; // This will be stored in vault
  serviceAddress?: string;
  billingAddress?: string;
  customerServicePhone?: string;
  emergencyPhone?: string;
  website?: string;
  meterNumber?: string;
  ratePlan?: string;
  averageMonthlyCost?: number;
  autopayEnabled?: boolean;
  autopayMethod?: string;
  contractEndDate?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class ElectricProviderInfoService {
  async getAllElectricProviderInfo(targetUserId?: string): Promise<ElectricProviderInfoRec[]> {
    const url = targetUserId
      ? `/api/electric-provider-info?targetUserId=${targetUserId}`
      : '/api/electric-provider-info';
    const response = await http.get(url);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch electric provider information');
    }

    return response.data.data || [];
  }

  async getElectricProviderInfoById(id: string): Promise<ElectricProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/electric-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch electric provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching electric provider information by ID:', error);
      return null;
    }
  }

  async createElectricProviderInfo(electricProviderData: Partial<ElectricProviderInfoRec>): Promise<ElectricProviderInfoRec> {
    const response = await http.post('/api/electric-provider-info', electricProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save electric provider information');
    }

    return response.data.data;
  }

  async updateElectricProviderInfo(id: string, electricProviderData: Partial<ElectricProviderInfoRec>): Promise<ElectricProviderInfoRec> {
    const response = await http.patch(`/api/electric-provider-info/${id}`, electricProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update electric provider information');
    }

    return response.data.data;
  }

  async deleteElectricProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/electric-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete electric provider information');
    }
  }

  async lockElectricProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for electric provider ID:', id);
    const response = await http.post(`/api/electric-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock electric provider information');
    }
  }

  async unlockElectricProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/electric-provider-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock electric provider information');
    }
  }

  async getElectricProviderAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/electric-provider-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const electricProviderInfoService = new ElectricProviderInfoService();
export default electricProviderInfoService;